import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Image, Video, Filter, GripVertical } from "lucide-react";
import { ImageUploader } from "./ImageUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PortfolioMedia {
  id: string;
  title: string;
  media_type: "image" | "video";
  url: string;
  thumbnail_url: string | null;
  alt_text: string | null;
  video_source: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  locations: string[];
}

const LOCATIONS = [
  { id: "homepage_hero", label: "Homepage Hero", description: "Main carousel on homepage" },
  { id: "about_page", label: "About Page", description: "Images on About Pixie Clicks" },
  { id: "our_story_page", label: "Our Story Page", description: "Images on Our Story" },
  { id: "photography_page", label: "Photography", description: "Photography service page" },
  { id: "cinematography_page", label: "Cinematography", description: "Cinematography service page" },
  { id: "combo_packages_page", label: "Combo Packages", description: "Combo packages page" },
  { id: "photobooth_page", label: "Photo Booth", description: "Photo booth page" },
  { id: "portfolio_page", label: "Portfolio", description: "Main portfolio page" },
] as const;

interface SortableRowProps {
  item: PortfolioMedia;
  onEdit: (item: PortfolioMedia) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, currentActive: boolean) => void;
}

const SortableRow = ({ item, onEdit, onDelete, onToggleActive }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? "bg-muted" : ""}>
      <TableCell className="w-10">
        <button
          className="cursor-grab touch-none p-1 hover:bg-muted rounded"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        {item.media_type === "image" ? (
          <img
            src={item.url}
            alt={item.alt_text || item.title}
            className="h-12 w-12 rounded object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
            <Video className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">{item.title}</TableCell>
      <TableCell>
        <Badge variant="outline" className="gap-1">
          {item.media_type === "image" ? (
            <Image className="h-3 w-3" />
          ) : (
            <Video className="h-3 w-3" />
          )}
          {item.media_type}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {item.locations.slice(0, 2).map((loc) => (
            <Badge key={loc} variant="secondary" className="text-xs">
              {LOCATIONS.find((l) => l.id === loc)?.label || loc}
            </Badge>
          ))}
          {item.locations.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{item.locations.length - 2}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Switch
          checked={item.is_active}
          onCheckedChange={() => onToggleActive(item.id, item.is_active)}
        />
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(item)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export const PortfolioManager = () => {
  const [media, setMedia] = useState<PortfolioMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState<PortfolioMedia | null>(null);
  const [filterLocation, setFilterLocation] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const { toast } = useToast();

  // Form state
  const [title, setTitle] = useState("");
  const [mediaType, setMediaType] = useState<"image" | "video">("image");
  const [url, setUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [altText, setAltText] = useState("");
  const [videoSource, setVideoSource] = useState<string>("knotflix");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    setIsLoading(true);
    try {
      // Fetch media with locations
      const { data: mediaData, error: mediaError } = await supabase
        .from("portfolio_media")
        .select("*")
        .order("sort_order", { ascending: true });

      if (mediaError) throw mediaError;

      // Fetch all locations
      const { data: locationsData, error: locationsError } = await supabase
        .from("portfolio_media_locations")
        .select("*");

      if (locationsError) throw locationsError;

      // Combine media with their locations
      const mediaWithLocations = (mediaData || []).map((m) => ({
        ...m,
        media_type: m.media_type as "image" | "video",
        locations: (locationsData || [])
          .filter((l) => l.media_id === m.id)
          .map((l) => l.location),
      }));

      setMedia(mediaWithLocations);
    } catch (error: any) {
      toast({ title: "Error loading media", description: error.message, variant: "destructive" });
    }
    setIsLoading(false);
  };

  const openDialog = (item?: PortfolioMedia) => {
    if (item) {
      setEditingMedia(item);
      setTitle(item.title);
      setMediaType(item.media_type);
      setUrl(item.url);
      setThumbnailUrl(item.thumbnail_url || "");
      setAltText(item.alt_text || "");
      setVideoSource(item.video_source || "knotflix");
      setSortOrder(item.sort_order);
      setIsActive(item.is_active);
      setSelectedLocations(item.locations);
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMedia(null);
    setTitle("");
    setMediaType("image");
    setUrl("");
    setThumbnailUrl("");
    setAltText("");
    setVideoSource("knotflix");
    setSortOrder(0);
    setIsActive(true);
    setSelectedLocations([]);
  };

  const toggleLocation = (locationId: string) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((l) => l !== locationId)
        : [...prev, locationId]
    );
  };

  const saveMedia = async () => {
    if (!title || !url) {
      toast({ title: "Title and URL are required", variant: "destructive" });
      return;
    }

    if (selectedLocations.length === 0) {
      toast({ title: "Please select at least one location", variant: "destructive" });
      return;
    }

    try {
      const mediaData = {
        title,
        media_type: mediaType,
        url,
        thumbnail_url: thumbnailUrl || null,
        alt_text: altText || null,
        video_source: mediaType === "video" ? videoSource : null,
        sort_order: sortOrder,
        is_active: isActive,
      };

      let mediaId = editingMedia?.id;

      if (editingMedia) {
        const { error } = await supabase
          .from("portfolio_media")
          .update(mediaData)
          .eq("id", editingMedia.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("portfolio_media")
          .insert(mediaData)
          .select()
          .single();
        if (error) throw error;
        mediaId = data.id;
      }

      // Update locations
      if (mediaId) {
        await supabase.from("portfolio_media_locations").delete().eq("media_id", mediaId);
        if (selectedLocations.length > 0) {
          await supabase.from("portfolio_media_locations").insert(
            selectedLocations.map((location) => ({
              media_id: mediaId,
              location,
            }))
          );
        }
      }

      toast({ title: editingMedia ? "Media updated!" : "Media added!" });
      setIsDialogOpen(false);
      fetchMedia();
    } catch (error: any) {
      toast({ title: "Error saving media", description: error.message, variant: "destructive" });
    }
  };

  const deleteMedia = async (id: string) => {
    if (!confirm("Delete this media item?")) return;

    try {
      const { error } = await supabase.from("portfolio_media").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Media deleted" });
      fetchMedia();
    } catch (error: any) {
      toast({ title: "Error deleting media", description: error.message, variant: "destructive" });
    }
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from("portfolio_media")
        .update({ is_active: !currentActive })
        .eq("id", id);
      if (error) throw error;
      fetchMedia();
    } catch (error: any) {
      toast({ title: "Error updating status", description: error.message, variant: "destructive" });
    }
  };

  const filteredMedia = media.filter((m) => {
    const matchesLocation = filterLocation === "all" || m.locations.includes(filterLocation);
    const matchesType = filterType === "all" || m.media_type === filterType;
    return matchesLocation && matchesType;
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredMedia.findIndex((item) => item.id === active.id);
      const newIndex = filteredMedia.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(filteredMedia, oldIndex, newIndex);

      // Optimistic update
      setMedia((prev) => {
        const otherMedia = prev.filter(
          (m) => !filteredMedia.some((fm) => fm.id === m.id)
        );
        return [...otherMedia, ...newOrder].sort((a, b) => {
          const aIndex = newOrder.findIndex((no) => no.id === a.id);
          const bIndex = newOrder.findIndex((no) => no.id === b.id);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      });

      // Persist to database
      try {
        const updates = newOrder.map((item, index) => ({
          id: item.id,
          sort_order: index,
        }));

        for (const update of updates) {
          await supabase
            .from("portfolio_media")
            .update({ sort_order: update.sort_order })
            .eq("id", update.id);
        }

        toast({ title: "Order updated" });
      } catch (error: any) {
        toast({
          title: "Error updating order",
          description: error.message,
          variant: "destructive",
        });
        fetchMedia(); // Revert on error
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-foreground">Portfolio Media</h3>
        <Button onClick={() => openDialog()} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Media
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Filter by location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {LOCATIONS.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Total Media</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{media.length}</p>
        </div>
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Images</p>
          <p className="mt-1 text-2xl font-bold text-foreground">
            {media.filter((m) => m.media_type === "image").length}
          </p>
        </div>
        <div className="rounded-xl bg-card border border-border p-4">
          <p className="text-sm font-medium text-muted-foreground">Videos</p>
          <p className="mt-1 text-2xl font-bold text-foreground">
            {media.filter((m) => m.media_type === "video").length}
          </p>
        </div>
      </div>

      {/* Media Table */}
      <div className="rounded-xl bg-card border border-border overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-16">Preview</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Locations</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={filteredMedia.map((m) => m.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredMedia.map((item) => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    onEdit={openDialog}
                    onDelete={deleteMedia}
                    onToggleActive={toggleActive}
                  />
                ))}
              </SortableContext>
              {filteredMedia.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No media items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMedia ? "Edit Media" : "Add Media"}</DialogTitle>
          </DialogHeader>

          <Tabs value={mediaType} onValueChange={(v) => setMediaType(v as "image" | "video")} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="image" className="gap-2">
                <Image className="h-4 w-4" />
                Image
              </TabsTrigger>
              <TabsTrigger value="video" className="gap-2">
                <Video className="h-4 w-4" />
                Video
              </TabsTrigger>
            </TabsList>

            <div className="space-y-6 pt-6">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Descriptive title"
                />
              </div>

              <TabsContent value="image" className="mt-0 space-y-4">
                <div>
                  <Label>Upload Image</Label>
                  <ImageUploader
                    bucket="gallery-images"
                    folder="portfolio"
                    value={url}
                    onChange={setUrl}
                  />
                </div>
                <div>
                  <Label htmlFor="altText">Alt Text</Label>
                  <Input
                    id="altText"
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    placeholder="Description for accessibility"
                  />
                </div>
              </TabsContent>

              <TabsContent value="video" className="mt-0 space-y-4">
                <div>
                  <Label htmlFor="videoSource">Video Source</Label>
                  <Select value={videoSource} onValueChange={setVideoSource}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="knotflix">Knotflix (Mux)</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="vimeo">Vimeo</SelectItem>
                      <SelectItem value="mux">Mux Direct</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="videoUrl">Video Embed URL</Label>
                  <Input
                    id="videoUrl"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={
                      videoSource === "knotflix"
                        ? "https://knotflix.com/embed/..."
                        : videoSource === "youtube"
                        ? "https://www.youtube.com/embed/..."
                        : "https://player.vimeo.com/video/..."
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste the full embed URL from {videoSource === "knotflix" ? "Knotflix" : videoSource}
                  </p>
                </div>
                <div>
                  <Label>Thumbnail (Optional)</Label>
                  <ImageUploader
                    bucket="gallery-images"
                    folder="portfolio-thumbnails"
                    value={thumbnailUrl}
                    onChange={setThumbnailUrl}
                  />
                </div>
              </TabsContent>

              {/* Locations */}
              <div className="border-t pt-4">
                <Label className="mb-3 block">Assign to Pages</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {LOCATIONS.map((location) => (
                    <div
                      key={location.id}
                      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        selectedLocations.includes(location.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                      onClick={() => toggleLocation(location.id)}
                    >
                      <Checkbox
                        checked={selectedLocations.includes(location.id)}
                        onCheckedChange={() => toggleLocation(location.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{location.label}</p>
                        <p className="text-xs text-muted-foreground">{location.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sort Order & Active */}
              <div className="flex items-center gap-6">
                <div className="flex-1">
                  <Label htmlFor="sortOrder">Sort Order</Label>
                  <Input
                    id="sortOrder"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                    min={0}
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                  <Label>Active</Label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveMedia}>
                  {editingMedia ? "Update Media" : "Add Media"}
                </Button>
              </div>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};
